import { POST } from '@/api'
import { keyBy, mapValues } from 'lodash'
import { eachTime, normalizeTime, toMinutes, minutesToTime } from '@/utils/time'

const INITIAL_BUSINESS_HOUR = {
  lunch: { start: null, end: null },
  dinner: { start: null, end: null },
}

const INITIAL_BUSINESS_HOURS = {
  1: { day: 1, ...INITIAL_BUSINESS_HOUR }, // 月曜,
  2: { day: 2, ...INITIAL_BUSINESS_HOUR }, // 火曜,
  3: { day: 3, ...INITIAL_BUSINESS_HOUR }, // 水曜,
  4: { day: 4, ...INITIAL_BUSINESS_HOUR }, // 木曜,
  5: { day: 5, ...INITIAL_BUSINESS_HOUR }, // 金曜,
  6: { day: 6, ...INITIAL_BUSINESS_HOUR }, // 土曜,
  0: { day: 0, ...INITIAL_BUSINESS_HOUR }, // 日曜
  '-1': { day: -1, ...INITIAL_BUSINESS_HOUR }, // 祝日
  '-2': { day: -2, ...INITIAL_BUSINESS_HOUR }, // 祝前日
}

const INITIAL_STORE = {
  id: null, // 店舗ID
  name: null, // 店舗名
  reservesView: 'table_list', // メインで使用する予約表示画面 `table_list, layout`

  // 時間
  time: {
    lunchUtilization: null, // ランチ利用時間
    dinnerUtilization: null, // ディナー利用時間
    reset: null, // リセットタイム
    dayDisplayTime: null, // テーブル一覧表示時間（当日）
    otherDisplayTime: null, // テーブル一覧表示時間（当日以外）
  },

  // 営業時間
  businessHours: {
    byDay: {},
  },

  // 件数
  counts: {
    info: 0, // お知らせ
    alert: 0, // アラート
  },

  // 使用中／利用可能／契約フラグ
  use: {
    layout: false, // テーブルレイアウト使用
    cti: false, // CTI 利用
    ivr: false, // IVR 契約
    import: false, // 他社取り込み契約
    toreta: false, // トレタデータ移行利用
    reception: false, // 台帳ネット予約契約
    gnaviCourse: false, // 台帳ネット予約でぐるなびのコースを使用
    reserveCsvDownload: false, // 予約CSVダウンロード
  },

  // 連携ステータス
  syncStatus: {
    gnavi: 0, // ぐるなび
    'pos+': 0, // POS+
    resraku: 0, // レスラク
  },

  // オプション項目定義
  optionsSchema: {
    customer: [],
    visitHistory: [],
  },

  // CSVダウンロード/アップロード画面トークン期限切れ検証
  isTokenAlive: false,
  // 通常店:0 / 高級店:1
  displayPattern: 0,
}

export default {
  namespaced: true,
  state: {
    store: {
      ...INITIAL_STORE,
    },
    busy: false,
  },
  mutations: {
    set(state, store) {
      state.store = {
        ...INITIAL_STORE,
        ...store,
        businessHours: {
          byDay: { ...INITIAL_BUSINESS_HOURS, ...keyBy(store.businessHours, 'day') },
        },
      }
      if (!state.store.use.layout) state.store.reservesView = 'table_list'
    },
    busy(state, busy) {
      state.busy = !!busy
    },
  },
  getters: {
    getZoneType: ({ store }) => (day, time) => {
      const { lunch, dinner } = store.businessHours.byDay[day]
      time = normalizeTime(time)
      if (!lunch || (dinner && normalizeTime(dinner.start) <= time && time <= normalizeTime(dinner.end)))
        return 'dinner'
      return !dinner || (normalizeTime(lunch.start) <= time && time <= normalizeTime(lunch.end)) ? 'lunch' : 'dinner'
    },

    /**
     * 営業時間内で予約受付設定が可能な時間の配列を、曜日・ゾーン（ランチ・ディナー）単位毎に返します。
     */
    canReceptTimesByZoneByDay: ({ store }) => {
      const { lunchUtilization, dinnerUtilization, reset } = store.time
      return mapValues(store.businessHours.byDay, ({ lunch, dinner }) => ({
        lunch: eachTime(lunch.start, minutesToTime(toMinutes(lunch.end) - lunchUtilization - reset)),
        dinner: eachTime(dinner.start, minutesToTime(toMinutes(dinner.end) - dinnerUtilization - reset)),
      }))
    },

    /**
     * 営業時間内で予約受付設定が可能な時間の配列を、曜日毎に返します。
     */
    canReceptTimesByDay: (state, getters) =>
      mapValues(getters.canReceptTimesByZoneByDay, ({ lunch, dinner }) => [...lunch, ...dinner]),
  },

  actions: {
    async get(store, { id, token = '' } = {}) {
      const { commit, state } = store
      if (state.busy) return false
      commit('busy', true)

      const { status, message, data } = await POST('/api/web/store/current', {
        id: id || state.store.id || undefined,
        token,
      })
      if (status === 200) {
        commit('set', data)
      } else {
        commit('app/error', { status, message }, { root: true })
      }

      commit('busy', false)
      return status === 200
    },
  },
}
